export interface LessonStep {
    title: string;
    description: string;
    imageUrl: string;
}

export interface LessonContent {
    id: string;
    title: string;
    markdown: string;
    steps?: LessonStep[];
}

export const LEARN_CONTENTS: Record<string, LessonContent> = {
    'getting-started': {
        id: 'getting-started',
        title: 'Getting Started with Magnus',
        markdown: `
# Welcome to Living Reliability
Magnus is more than just an FMEA tool. it is a **Living System** where data flows seamlessly between design, process, and field performance.

### Key Concepts:
*   **Single Source of Truth**: Data entered in Core reflects in PFMEA.
*   **AI-Native**: Use our agents to draft your first analysis in seconds.
*   **Always-in-Sync**: Cross-app data binding ensures compliance.
        `,
        steps: [
            {
                title: 'Secure Sign-In',
                description: 'Experience the smooth single sign-on animation as you enter the Magnus ecosystem.',
                imageUrl: '/learn/magnus_core_login_1773450460106.png' // Would be replaced with .gif
            }
        ]
    },
    'fmea-fundamentals': {
        id: 'fmea-fundamentals',
        title: 'FMEA Fundamentals',
        markdown: `
# FMEA: The Basics
Failure Mode and Effects Analysis (FMEA) is a structured approach to discovering potential failures that may exist within the design of a product or process.

### The AIAG-VDA 7-Step Process:
1. **Planning & Preparation**
2. **Structure Analysis**
3. **Function Analysis**
4. **Failure Analysis**
5. **Risk Analysis**
6. **Optimization**
7. **Results Documentation**
        `,
        steps: [
            {
                title: 'Risk Identification',
                description: 'Watch how the AI identifies and highlights potential failure modes in real-time.',
                imageUrl: '/learn/magnus_pfmea_ideation_wizard_1773450535322.png' // Would be replaced with .gif
            }
        ]
    },
    'scoping-wizard': {
        id: 'scoping-wizard',
        title: 'Mastering the Ideation Wizard',
        markdown: `
# The AI Ideation Wizard
The Ideation Wizard is designed to jumpstart your PFMEA by generating a draft process flow and potential failure modes from a simple text description.

### Best Practices:
*   **Be Specific**: Mention machines, temperatures, and tolerances.
*   **Refine in Steps**: Don't be afraid to ask the AI to "add more detail" to a specific step.
*   **4M Alignment**: AI automatically categorizes steps into Man, Machine, Material, or Method.
        `,
        steps: [
            {
                title: 'Step 1: Description',
                description: 'See how the AI parses your natural language input to build a structured process.',
                imageUrl: '/learn/magnus_pfmea_ideation_wizard_1773450535322.png'
            },
            {
                title: 'Step 2: Refinement',
                description: 'Watch the "Refine" animation as the AI updates steps based on your feedback.',
                imageUrl: '/learn/magnus_pfmea_ideation_wizard_1773450535322.png'
            }
        ]
    },
    'resolution-sync': {
        id: 'resolution-sync',
        title: 'Closing the Loop: Resolution Sync',
        markdown: `
# Resolution -> FMEA Sync
In the "Living Reliability" system, field failures found in **Magnus Resolve** are automatically suggested as new failure modes or causes in your **PFMEA**.
        `,
        steps: [
            {
                title: 'Incident Analysis',
                description: 'Observe the drag-and-drop interaction between field incidents and PFMEA worksheet.',
                imageUrl: '/learn/magnus_resolve_dashboard_1773450709989.png'
            }
        ]
    },
    'fmea-structure-function': {
        id: 'fmea-structure-function',
        title: 'Structure & Function (Step 2 & 3)',
        markdown: `
# Mastering Structure & Function
Modern AIAG-VDA PFMEA requires a clear link between the process structure and the intended functions.

### The Magnus Approach:
*   **Visual Structure**: Build your process tree visually in the Process Flow tab.
*   **Function Matrix**: Define what each step *should* do before identifying what can go wrong.
*   **4M Binding**: Linking functions to specific Man/Machine/Material elements.
        `,
        steps: [
            {
                title: 'Building the Matrix',
                description: 'Use the interactive matrix to map functions to process elements.',
                imageUrl: '/learn/magnus_pfmea_dashboard_1773450513054.png'
            }
        ]
    }
};
